import { useContext, useEffect } from 'react'
import { AuthContext } from '@/context/auth-context'
import { kDefaultLogoPath } from '@/constants/constants-ui'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import { setStripePortalDialogVisible } from '@/store/slices/ui-state.slice'
import { AccountActions } from '@/account/store/slice'
import { selectDisplayCostPerSeatInCents } from '@/account/store/selectors'
import { RootState } from '@/store/store'
import { formatCentsToDollarsCompact } from '@/util/money'
import { capitalizeFirstLetters } from '@/util/string-utils'
import { useOrganizationDetails } from './useOrganizationDetails'
import { useOrganizationDirectoryUsers } from './useOrganizationDirectoryUsers'
import { useRosterMembers } from './useRosterMembers'
import { useOrgLogoUpload } from './useOrgLogoUpload'
import type { MemberRosterMember, BillingStats } from '@/organizations/views/MembersRoster'

export type MembersRosterData = {
  /** Current user is an org account (admin or member); when false the roster has nothing to show. */
  isOrgAccount: boolean
  isOrgAdmin: boolean
  isOrgPayer: boolean
  orgName: string
  orgLogoUrl?: string
  currentUserId: string
  members: MemberRosterMember[]
  memberCount: number
  /** Admins only; members get no billing figures. */
  billingStats?: BillingStats
  isError: boolean
  isLogoBusy: boolean
  uploadLogoFile: (file: File) => void
  deleteLogo: () => void
  /** Refetch the roster after a member/invite mutation. */
  refetch: () => void
  /** Opens the Stripe billing portal; only present for the org payer. */
  onManageSubscription?: () => void
}

/**
 * Assembles everything the Members panels need: org-detail vs directory fetch by role, the shaped
 * member list, billing stats, logo upload, and billing refresh — mirroring what SidebarAccountMenu
 * does inline for the standalone roster. Lives in `organizations/` so the settings-revamp shell only
 * consumes it. Call it from a component that renders only when its section is active (the hook fires
 * its fetches on mount).
 */
type UseMembersRosterDataOptions = {
  /**
   * Force a fresh payment-details fetch on mount instead of honoring the store cache. The dedicated
   * Billing panel passes `true` so its figures are current; the roster panels keep the cached default.
   */
  forcePaymentRefresh?: boolean
}

export function useMembersRosterData({ forcePaymentRefresh = false }: UseMembersRosterDataOptions = {}): MembersRosterData {
  const { user, userAccountData, logoUrl } = useContext(AuthContext)
  const dispatch = useAppDispatch()

  const isOrgAccount = !!userAccountData?.isOrgAccount
  const isOrgAdmin = !!userAccountData?.isOrgAdmin
  const isOrgPayer = !!userAccountData?.isOrgPayer
  const canViewSubscriptionInfo = !!userAccountData?.canViewStripeSubscriptionInfo

  const seatCostCents = useAppSelector(selectDisplayCostPerSeatInCents)
  const subscriptionInterval = useAppSelector((s: RootState) => s.accountState.stripeSubscriptionInterval)

  // Only surface a real custom logo; fetchOrgLogoAsUrl falls back to the default Paxton path when none exists.
  const orgLogoUrl = logoUrl !== kDefaultLogoPath ? logoUrl : undefined

  // Admin gets full details + invites; member gets the directory listing. Only one query is ever enabled.
  const { data: orgDetails, refetch: refetchOrgDetails, isError: orgDetailsIsError } = useOrganizationDetails({ enabled: isOrgAccount && isOrgAdmin })
  const { data: directoryUsers, isError: directoryUsersIsError } = useOrganizationDirectoryUsers({ enabled: isOrgAccount && !isOrgAdmin })
  const isError = orgDetailsIsError || directoryUsersIsError

  const { isBusy: isLogoBusy, uploadLogoFile, deleteLogo } = useOrgLogoUpload()

  const members = useRosterMembers({ isOrgAdmin, orgDetails, directoryUsers })
  const memberCount = members.filter((member) => !member.isPending).length

  const billingStats: BillingStats = {
    paidSeats: userAccountData?.orgData?.seats ?? 0,
    pendingInviteCount: orgDetails?.organization.invites.length ?? 0,
    billingCadence: capitalizeFirstLetters(userAccountData?.billingCycle ?? '-'),
    seatCost: seatCostCents != null && subscriptionInterval ? `${formatCentsToDollarsCompact(seatCostCents)} / ${subscriptionInterval}` : '-',
  }

  // Refresh billing + roster when the panel first mounts so figures and accepted invites are current.
  useEffect(() => {
    if (isOrgAccount && canViewSubscriptionInfo) {
      dispatch(AccountActions.refreshPaymentDetails({ forceRefresh: forcePaymentRefresh }))
    }
  }, [isOrgAccount, canViewSubscriptionInfo, forcePaymentRefresh, dispatch])

  useEffect(() => {
    if (isOrgAccount && isOrgAdmin) {
      refetchOrgDetails()
    }
  }, [isOrgAccount, isOrgAdmin, refetchOrgDetails])

  return {
    isOrgAccount,
    isOrgAdmin,
    isOrgPayer,
    orgName: userAccountData?.orgData?.name ?? '',
    orgLogoUrl,
    currentUserId: user?.uid ?? '',
    members,
    memberCount,
    billingStats: isOrgAdmin ? billingStats : undefined,
    isError,
    isLogoBusy,
    uploadLogoFile,
    deleteLogo,
    refetch: refetchOrgDetails,
    onManageSubscription: isOrgPayer ? () => dispatch(setStripePortalDialogVisible(true)) : undefined,
  }
}
